/**
 * Abgleich mit dem Server (PV-B12 bis PV-B15).
 *
 * Reihenfolge: erst senden, dann holen. Andersherum wuerde eine gerade
 * geschriebene Zeile vom Serverstand ueberschrieben, bevor sie ueberhaupt
 * angekommen ist.
 *
 * Ueberschrieben wird lokal nur, was nicht schmutzig ist. Wer offline etwas
 * geaendert hat, verliert es nicht dadurch, dass jemand anderer dieselbe Zeile
 * angefasst hat — dann gibt es einen Konflikt, und der wird angezeigt (PV-B14).
 */

import type { Table } from 'dexie';

import { mitTagen } from '../datum.ts';
import { db, ABGLEICHSTAND, ANMELDUNG, leseZustand, schreibeZustand } from './db.ts';
import type { Ausgangseintrag, Konflikt } from '../typen.ts';

export type Abgleichzustand = 'offen' | 'uebertraegt' | 'abgeglichen' | 'fehlgeschlagen';

export interface Abgleichbericht {
  zustand: Abgleichzustand;
  gesendet: number;
  geholt: number;
  konflikte: number;
  stand: number;
  meldung?: string;
  /** Die Sitzung gilt nicht mehr — die Oberflaeche gehoert zurueck zur Anmeldung. */
  abgemeldet?: boolean;
}

interface Anmeldung {
  token: string;
  benutzer: { id: string; name: string; email: string; rolle: string };
}

export async function anmeldung(): Promise<Anmeldung | null> {
  return leseZustand<Anmeldung | null>(ANMELDUNG, null);
}

/**
 * Wird geworfen, wenn die Sitzung nicht mehr gilt.
 *
 * Das kommt vor: Die Sitzung laeuft nach dreissig Tagen ab, ein Verwalter legt
 * jemanden still oder aendert seine Rolle — dann sind die offenen Sitzungen
 * absichtlich weg. Vorher meldete die Statusleiste dann nur "Abgleich
 * fehlgeschlagen" und blieb dabei; man sass vor einem Programm, das nichts mehr
 * uebertrug, ohne zu erfahren warum.
 */
export class SitzungAbgelaufen extends Error {
  constructor() {
    super('Die Sitzung gilt nicht mehr. Bitte neu anmelden.');
    this.name = 'SitzungAbgelaufen';
  }
}

/** Angemeldet beim eigenen Server anfragen — fuer alles ausserhalb des Abgleichs. */
export const rufeServer = (pfad: string, optionen: RequestInit = {}): Promise<Response> =>
  ruf(pfad, optionen);

async function ruf(pfad: string, optionen: RequestInit = {}): Promise<Response> {
  const an = await anmeldung();
  const kopf = new Headers(optionen.headers);
  // JSON, ausser es ist ausdruecklich etwas anderes gesagt — ein Bild fuer ein
  // Ticket geht als Bild.
  if (!kopf.has('Content-Type')) kopf.set('Content-Type', 'application/json');
  if (an) kopf.set('Authorization', `Bearer ${an.token}`);
  const antwort = await fetch(pfad, { ...optionen, headers: kopf });
  if (antwort.status === 401) {
    // Die abgelegte Anmeldung ist wertlos — weg damit, sonst probiert es das
    // Geraet bei jedem Abgleich wieder und meldet dasselbe.
    await schreibeZustand(ANMELDUNG, null);
    throw new SitzungAbgelaufen();
  }
  return antwort;
}

export async function meldeAn(email: string, passwort: string): Promise<Anmeldung> {
  const antwort = await fetch('/api/anmeldung', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, passwort, geraet: geraetename() }),
  });
  if (!antwort.ok) throw new Error('Anmeldung fehlgeschlagen');
  const daten = (await antwort.json()) as Anmeldung;
  await schreibeZustand(ANMELDUNG, daten);
  return daten;
}

export function geraetename(): string {
  const kennung = navigator.userAgent;
  if (/iPad|iPhone/.test(kennung)) return 'iPad/iPhone';
  if (/Android/.test(kennung)) return 'Android';
  if (/Macintosh/.test(kennung)) return 'Mac';
  if (/Windows/.test(kennung)) return 'Windows';
  return 'Unbekanntes Gerät';
}

/**
 * Die Tabellen, die am Abgleich teilnehmen.
 *
 * Bewusst auf einen gemeinsamen Zeilentyp gebracht: Der Abgleich schaufelt
 * Zeilen hin und her, ohne sie zu kennen. Ohne diese Vereinheitlichung baut
 * TypeScript aus einem Dutzend Tabellentypen eine Union, die es selbst nicht
 * mehr darstellen kann.
 */
type Abgleichzeile = Record<string, unknown>;

const TABELLEN: Record<string, () => Table<Abgleichzeile, string>> = {
  projekte: () => db.projekte,
  flaechen: () => db.flaechen,
  belegungen: () => db.belegungen,
  fotos: () => db.fotos,
  strings: () => db.strings,
  projekt_komponenten: () => db.projekt_komponenten,
  leitungswege: () => db.leitungswege,
  materialpositionen: () => db.materialpositionen,
  wirtschaftlichkeit: () => db.wirtschaftlichkeit,
  verschattungsobjekte: () => db.verschattungsobjekte,
  kartenmarken: () => db.kartenmarken,
  projektschritte: () => db.projektschritte,
  dokumente: () => db.dokumente,
  auftraege: () => db.auftraege,
  pruefbefunde: () => db.pruefbefunde,
  projekteinstellungen: () => db.projekteinstellungen,
  // Stammdaten: werden geholt und — mit der passenden Rolle — auch geschrieben.
  module: () => db.module,
  komponenten: () => db.komponenten,
  artikel: () => db.artikel,
  materialsaetze: () => db.materialsaetze,
  checklistenvorlagen: () => db.checklistenvorlagen,
  // Angebote werden ueber /api/angebote angelegt — Nummernvergabe gehoert auf
  // die Serverseite. Geholt werden sie ganz normal.
  angebote: () => db.angebote,
} as unknown as Record<string, () => Table<Abgleichzeile, string>>;

/**
 * Ein Abgleich zur Zeit. Der Knopf, das Wiederkommen der Verbindung und der
 * Zeitgeber konnten zwei gleichzeitig anstoßen — beide schickten dieselbe
 * Warteschlange, und der zweite bekam für jede Zeile einen Konflikt mit dem
 * ersten.
 */
let laufenderAbgleich: Promise<Abgleichbericht> | null = null;

export function gleicheAb(): Promise<Abgleichbericht> {
  laufenderAbgleich ??= gleicheAbEinmal().finally(() => {
    laufenderAbgleich = null;
  });
  return laufenderAbgleich;
}

async function gleicheAbEinmal(): Promise<Abgleichbericht> {
  const stand = await leseZustand(ABGLEICHSTAND, 0);
  if (!navigator.onLine) {
    return {
      zustand: 'offen',
      gesendet: 0,
      geholt: 0,
      konflikte: 0,
      stand,
      meldung: 'Keine Verbindung. Die Änderungen liegen bereit.',
    };
  }

  try {
    const gesendet = await sende();
    const geholt = await hole();
    await holeDatenblattliste();
    // Erst nach dem Senden: Ein Foto kann nur hochgeladen werden, wenn der
    // Server den zugehoerigen Datensatz schon kennt.
    const fotos = await sendeFotos();
    const dokumente = await sendeDokumente();
    const dateienOffen = fotos.fehlgeschlagen + dokumente.fehlgeschlagen;
    if (dateienOffen > 0 && gesendet.konflikte === 0) {
      // Die Fachdaten sind abgeglichen, aber Dateien liegen noch am Gerät —
      // das darf nicht als „abgeglichen“ durchgehen.
      return {
        zustand: 'fehlgeschlagen',
        gesendet: gesendet.angenommen,
        geholt: geholt.zeilen,
        konflikte: 0,
        stand: geholt.stand,
        meldung: `${dateienOffen} Datei(en) nicht hochgeladen — sie bleiben am Gerät und gehen beim nächsten Abgleich mit.`,
      };
    }
    return {
      zustand: gesendet.konflikte > 0 ? 'fehlgeschlagen' : 'abgeglichen',
      gesendet: gesendet.angenommen,
      geholt: geholt.zeilen,
      konflikte: gesendet.konflikte,
      stand: geholt.stand,
      ...(gesendet.konflikte > 0
        ? { meldung: `${gesendet.konflikte} Konflikt(e) — bitte von Hand entscheiden.` }
        : {}),
    };
  } catch (fehler) {
    return {
      zustand: 'fehlgeschlagen',
      gesendet: 0,
      geholt: 0,
      konflikte: 0,
      stand,
      meldung: fehler instanceof Error ? fehler.message : String(fehler),
      ...(fehler instanceof SitzungAbgelaufen ? { abgemeldet: true } : {}),
    };
  }
}

/** Schlüssel im lokalen Zustand: die Liste der Datenblätter, ganz geholt. */
export const DATENBLAETTER = 'datenblaetter';

/**
 * Die Liste ist klein und wird bei jedem Abgleich ganz geholt. Fehlt sie oder
 * scheitert der Abruf, bleibt die alte stehen — der Abgleich der Fachdaten
 * hängt nicht daran.
 */
async function holeDatenblattliste(): Promise<void> {
  try {
    const antwort = await ruf('/api/datenblaetter');
    if (!antwort.ok) return;
    const { datenblaetter } = (await antwort.json()) as { datenblaetter: unknown[] };
    await schreibeZustand(DATENBLAETTER, datenblaetter);
  } catch (fehler) {
    if (fehler instanceof SitzungAbgelaufen) throw fehler;
  }
}

async function sende(): Promise<{ angenommen: number; konflikte: number }> {
  const offen = await db.ausgang.orderBy('nummer').toArray();
  if (offen.length === 0) return { angenommen: 0, konflikte: 0 };

  const antwort = await ruf('/api/sync/senden', {
    method: 'POST',
    body: JSON.stringify({
      aenderungen: offen.map((eintrag: Ausgangseintrag) => ({
        tabelle: eintrag.tabelle,
        id: eintrag.id,
        basisSeq: eintrag.basisSeq,
        daten: eintrag.daten,
      })),
    }),
  });
  if (!antwort.ok) throw new Error(`Senden fehlgeschlagen (${antwort.status})`);

  const ergebnis = (await antwort.json()) as {
    angenommen: Array<{ tabelle: string; id: string; seq: number }>;
    konflikte: Array<{
      tabelle: string;
      id: string;
      serverSeq: number;
      serverdaten: Record<string, unknown>;
    }>;
    abgewiesen: Array<{ tabelle: string; id: string; grund: string }>;
  };

  // Was gesendet wurde, je Zeile — um zu erkennen, ob während der Anfrage
  // weiter getippt wurde.
  const gesendet = new Map(offen.map((e) => [`${e.tabelle}|${e.id}`, e]));
  const unveraendert = (jetzt: Ausgangseintrag | undefined, damals: Ausgangseintrag | undefined): boolean =>
    !!jetzt && !!damals && jetzt.angelegt_am === damals.angelegt_am && JSON.stringify(jetzt.daten) === JSON.stringify(damals.daten);

  for (const angenommen of ergebnis.angenommen) {
    const tabelle = TABELLEN[angenommen.tabelle];
    if (!tabelle) continue;
    await db.transaction('rw', tabelle() as Table<unknown, string>, db.ausgang, async () => {
      const eintrag = await db.ausgang.where('[tabelle+id]').equals([angenommen.tabelle, angenommen.id]).first();
      if (eintrag?.nummer !== undefined && !unveraendert(eintrag, gesendet.get(`${angenommen.tabelle}|${angenommen.id}`))) {
        // Während der Anfrage kam eine Änderung dazu. Sie bleibt in der
        // Warteschlange und setzt auf dem Stand auf, den der Server eben
        // angenommen hat — vorher wurde sie mit gelöscht und ging nie hinaus.
        await db.ausgang.update(eintrag.nummer, { basisSeq: angenommen.seq });
        await tabelle().update(angenommen.id, { seq: angenommen.seq });
        return;
      }
      await tabelle().update(angenommen.id, { seq: angenommen.seq, schmutzig: false });
      await db.ausgang.where('[tabelle+id]').equals([angenommen.tabelle, angenommen.id]).delete();
    });
  }

  for (const konflikt of ergebnis.konflikte) {
    const tabelle = TABELLEN[konflikt.tabelle];
    const meine = tabelle ? await tabelle().get(konflikt.id) : undefined;
    await vermerke({
      tabelle: konflikt.tabelle,
      datensatz_id: konflikt.id,
      meine: (meine ?? {}) as Record<string, unknown>,
      server: konflikt.serverdaten,
      bemerkt_am: new Date().toISOString(),
      art: 'konflikt',
      serverSeq: konflikt.serverSeq,
    });
    // Der Eintrag bleibt in der Warteschlange liegen, bis entschieden ist.
  }

  // Abgewiesenes wurde bisher verschluckt. Der Server nimmt die Zeile dauerhaft
  // nicht an — sie blieb trotzdem in der Warteschlange, und "3 offen" ging nie
  // mehr weg, egal wie oft abgeglichen wurde. Jetzt kommt sie heraus und wird
  // zur Entscheidung vorgelegt.
  for (const abgewiesen of ergebnis.abgewiesen) {
    const tabelle = TABELLEN[abgewiesen.tabelle];
    const meine = tabelle ? await tabelle().get(abgewiesen.id) : undefined;
    await vermerke({
      tabelle: abgewiesen.tabelle,
      datensatz_id: abgewiesen.id,
      meine: (meine ?? {}) as Record<string, unknown>,
      server: {},
      bemerkt_am: new Date().toISOString(),
      art: 'abgewiesen',
      grund: abgewiesen.grund,
    });
    await db.ausgang.where('[tabelle+id]').equals([abgewiesen.tabelle, abgewiesen.id]).delete();
  }

  return {
    angenommen: ergebnis.angenommen.length,
    konflikte: ergebnis.konflikte.length + ergebnis.abgewiesen.length,
  };
}

/**
 * Einen Konflikt vermerken — je Datensatz genau einmal.
 *
 * Vorher legte jeder Abgleichversuch denselben Konflikt noch einmal an. Aus
 * drei offenen Aenderungen wurden so nach elf Versuchen 34 Konflikte, und die
 * Zahl in der Statusleiste sagte nichts mehr aus.
 */
async function vermerke(konflikt: Konflikt): Promise<void> {
  const vorhanden = await db.konflikte
    .where('[tabelle+datensatz_id]')
    .equals([konflikt.tabelle, konflikt.datensatz_id])
    .first();
  if (vorhanden?.id !== undefined) {
    await db.konflikte.put({ ...konflikt, id: vorhanden.id });
    return;
  }
  await db.konflikte.add(konflikt);
}

/**
 * Einen Konflikt entscheiden.
 *
 * 'server': Der Serverstand gilt. Die eigene Aenderung faellt weg.
 * 'meine':  Die eigene Fassung gilt. Sie setzt auf dem Serverstand auf, wird
 *           also beim naechsten Abgleich angenommen statt wieder abgelehnt.
 */
/**
 * Stammdaten gelten fuer alle Projekte. Aus einem Projekt heraus darf der
 * Serverstand deshalb nicht ueberschrieben werden — geaendert wird die
 * Preisliste ausschliesslich in den Einstellungen, bewusst und an einer Stelle.
 * Wer hier einen Konflikt hat, nimmt den Serverstand; braucht er fuer sein
 * Projekt einen anderen Preis, setzt er ihn als Projektpreis
 * (preise.ueberschreibungen), ohne die Liste der Firma anzufassen.
 *
 * Die Sperre steht hier und nicht nur am Knopf: Was die Oberflaeche gerade
 * anzeigt, darf nicht entscheiden, was erlaubt ist.
 */
const STAMMDATEN = new Set(['module', 'komponenten', 'artikel', 'materialsaetze', 'einstellungen']);

export async function entscheideKonflikt(
  id: number,
  wahl: 'meine' | 'server',
): Promise<void> {
  const konflikt = await db.konflikte.get(id);
  if (!konflikt) return;
  if (wahl === 'meine' && STAMMDATEN.has(konflikt.tabelle)) {
    throw new Error(
      'Stammdaten lassen sich aus einem Projekt heraus nicht überschreiben. Der Serverstand gilt; ein abweichender Preis gehört als Projektpreis in die Einstellungen.',
    );
  }
  const tabelle = TABELLEN[konflikt.tabelle];

  if (wahl === 'server') {
    await db.ausgang
      .where('[tabelle+id]')
      .equals([konflikt.tabelle, konflikt.datensatz_id])
      .delete();
    if (tabelle && Object.keys(konflikt.server).length > 0) {
      await tabelle().put({ ...konflikt.server, schmutzig: false });
    }
  } else if (konflikt.art !== 'abgewiesen') {
    // Auf dem Serverstand aufsetzen — dann greift die Konflikterkennung nicht
    // mehr, und die eigene Fassung geht durch.
    const offen = await db.ausgang
      .where('[tabelle+id]')
      .equals([konflikt.tabelle, konflikt.datensatz_id])
      .first();
    // Die Laufnummer steht auch in den Serverdaten. Konflikte, die vor dieser
    // Fassung vermerkt wurden, haben serverSeq noch nicht — ohne den Rueckgriff
    // waeren sie nicht aufloesbar.
    const seq =
      konflikt.serverSeq ??
      (typeof konflikt.server['seq'] === 'number' ? (konflikt.server['seq'] as number) : null);
    if (offen?.nummer !== undefined) {
      await db.ausgang.update(offen.nummer, { basisSeq: seq });
    }
  }
  // Bei 'abgewiesen' und 'meine' gibt es nichts zu senden: Der Server nimmt
  // die Zeile so nicht an. Der Vermerk verschwindet, die lokale Zeile bleibt.
  await db.konflikte.delete(id);
}

async function hole(): Promise<{ zeilen: number; stand: number }> {
  const seit = await leseZustand(ABGLEICHSTAND, 0);
  const antwort = await ruf(`/api/sync/holen?seit=${seit}`);
  if (!antwort.ok) throw new Error(`Holen fehlgeschlagen (${antwort.status})`);

  const paket = (await antwort.json()) as {
    stand: number;
    tabellen: Record<string, Array<Record<string, unknown>>>;
  };

  let zeilen = 0;
  for (const [name, saetze] of Object.entries(paket.tabellen)) {
    if (saetze.length === 0) continue;
    const tabelle = name === 'einstellungen' ? () => db.einstellungen : TABELLEN[name];
    if (!tabelle) continue;
    const schluesselfeld = name === 'einstellungen' ? 'schluessel' : 'id';

    // Prüfen und Schreiben in einer Transaktion: Dazwischen darf keine
    // Eingabe landen, die das Holen dann überschreibt.
    await db.transaction('rw', tabelle() as Table<unknown, string>, db.ausgang, async () => {
      for (const satz of saetze) {
        const id = String(satz[schluesselfeld]);
        const vorhanden = (await tabelle().get(id)) as { schmutzig?: boolean } | undefined;
        // Lokale, noch nicht uebertragene Arbeit wird nie stillschweigend
        // ueberschrieben — auch bei den Einstellungen nicht, die wurden bisher
        // ungeprueft ersetzt. Der Konflikt faellt beim naechsten Senden auf.
        if (vorhanden?.schmutzig) continue;
        if ((await db.ausgang.where('[tabelle+id]').equals([name, id]).count()) > 0) continue;
        if (name === 'einstellungen') {
          await db.einstellungen.put({ schluessel: id, wert: satz['wert'], seq: Number(satz['seq']) });
        } else {
          // Ein Server vor 0.17 liefert Datumsfelder als Zeitstempel — siehe datum.ts.
          await (tabelle() as Table<unknown, string>).put({ ...mitTagen(name, satz), schmutzig: false });
        }
        zeilen++;
      }
    });
  }

  await schreibeZustand(ABGLEICHSTAND, paket.stand);
  return { zeilen, stand: paket.stand };
}

/**
 * Fotos nachschicken (PV-B09, PV-B11).
 *
 * Die Aufnahme wartet nie auf den Upload. Und der lokale Platz wird erst
 * freigegeben, wenn der Server den Eingang bestaetigt hat — sonst ist das Foto
 * weg, sobald der Speicher knapp wird.
 */
async function sendeFotos(): Promise<{ hochgeladen: number; fehlgeschlagen: number }> {
  const offen = await db.fotos.filter((f) => !f.hochgeladen && f.seq !== null).toArray();
  let hochgeladen = 0;
  let fehlgeschlagen = 0;

  for (const foto of offen) {
    const datei = await db.fotodateien.get(foto.id);
    if (!datei) {
      // Kein Bild mehr da, aber als nicht hochgeladen vermerkt: nichts zu tun,
      // sonst laeuft die Schleife bei jedem Abgleich wieder an.
      await db.fotos.update(foto.id, { hochgeladen: true });
      continue;
    }
    const an = await anmeldung();
    const antwort = await fetch(`/api/fotos/${foto.id}/datei?endung=jpg`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'image/jpeg',
        ...(an ? { Authorization: `Bearer ${an.token}` } : {}),
      },
      body: datei.blob,
    });
    if (antwort.status === 401) throw new SitzungAbgelaufen();
    // Ein Foto, das der Server nicht nimmt, hält die übrigen nicht auf.
    if (!antwort.ok) {
      fehlgeschlagen++;
      continue;
    }

    // Der Server zählt die Zeile beim Eingang der Datei weiter. Ohne die neue
    // Nummer liefe die nächste Änderung am Foto in einen Konflikt mit sich selbst.
    const { seq } = (await antwort.json().catch(() => ({}))) as { seq?: number };
    await db.fotos.update(foto.id, { hochgeladen: true, ...(typeof seq === 'number' ? { seq } : {}) });
    await db.fotodateien.delete(datei.foto_id);
    hochgeladen++;
  }
  return { hochgeladen, fehlgeschlagen };
}

/**
 * Dokumente nachschicken — derselbe Weg wie bei den Fotos. Die Datei des
 * Wechselrichters entsteht vor Ort, oft ohne Empfang; sie wartet am Gerät, bis
 * der Server den Eingang bestätigt hat.
 */
async function sendeDokumente(): Promise<{ hochgeladen: number; fehlgeschlagen: number }> {
  const offen = await db.dokumente.filter((d) => !d.hochgeladen && d.seq !== null).toArray();
  let hochgeladen = 0;
  let fehlgeschlagen = 0;
  for (const dok of offen) {
    const datei = await db.dokumentdateien.get(dok.id);
    if (!datei) {
      await db.dokumente.update(dok.id, { hochgeladen: true });
      continue;
    }
    const an = await anmeldung();
    const antwort = await fetch(`/api/dokumente/${dok.id}/datei`, {
      method: 'PUT',
      headers: {
        'Content-Type': dok.mime || 'application/octet-stream',
        ...(an ? { Authorization: `Bearer ${an.token}` } : {}),
      },
      body: datei.blob,
    });
    if (antwort.status === 401) throw new SitzungAbgelaufen();
    if (!antwort.ok) {
      fehlgeschlagen++;
      continue;
    }
    const { seq } = (await antwort.json().catch(() => ({}))) as { seq?: number };
    await db.dokumente.update(dok.id, { hochgeladen: true, ...(typeof seq === 'number' ? { seq } : {}) });
    await db.dokumentdateien.delete(dok.id);
    hochgeladen++;
  }
  return { hochgeladen, fehlgeschlagen };
}

/** Wie viele Aenderungen warten? Steht in der Statusleiste (PV-B15). */
export async function offenerStand(): Promise<{ offen: number; konflikte: number }> {
  return { offen: await db.ausgang.count(), konflikte: await db.konflikte.count() };
}
